<template lang="pug">
.separate-fileselect-table-component
  //- テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :filter-included-fields="filterFields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- ヘッダー：利用者グループ
    template(#head(group)="data")
      .d-flex.flex-column.justify-content-center
        span 利用者グループ
        span 部屋名・利用者名
    
    //- セル：利用者グループ
    template(#cell(group)="row")
      .d-flex.flex-column
        span {{ row.item.groupName }}
        span {{ row.item.room }}・{{ row.item.residentName }}
    //- セル：宛先
    template(#cell(targets)="row")
      .d-flex.flex-column
        span(v-for="family in row.item.targetFamilies") {{ family.label }}
    //- セル：ファイル選択
    template(#cell(selectFiles)="row")
      MultiFileSelecter(:mimetypes="['application/pdf']" width="300px" :limit="9" :showPreview="true"
        :enablePreviewStatus="enablePreview(row.item)" @input="inputFiles($event, row.item)" @preview="onPreview(row.item)")
      input(v-model="row.item.files" style="display: none")
      .error-messages.text-break(v-for="error in errors.collect(row.item.residentSortKey)")
        label.error-message {{ error }}
        br
    //- セル：登録ファイル
    template(#cell(files)="row")
      DraggableFiles(v-model="row.item.files" @delete="onFileDelete($event, row.item)" :item-width="'400px'" :popoverPrefix="row.item.residentSortKey")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  SendMessageFilePreviewModal(ref="previewModal")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import S3UrlService from "@/commons/services/S3Url";

import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";
import { SendTarget } from "@/pc/components/forms/FormSendTarget.vue";

import PageLoading from "@/pc/basics/PageLoading.vue";
import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";

/**
 * ### 文書送付、「利用者毎に異なる文書」のファイル選択テーブル
 */
@Component({
  components: {
    PageLoading,
    MultiFileSelecter,
    DraggableFiles,
    SendMessageFilePreviewModal
  }
})
export default class SeparateFileSelectTable extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<SeparateFileSelectTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<SeparateFileSelectTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** フィルター対象カラム */
  @Prop({ required: true })
  filterFields!: string[];

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  get _value(): Array<SeparateFileSelectTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: SeparateFileSelectTableItem): boolean {
    if (row.groupId == this.filter) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   */
  public async inputFiles(files: Array<File>, item: SeparateFileSelectTableItem): Promise<void> {
    const validFiles: Array<File> = await this.validation(files, item);

    if (validFiles.length < 1) {
      return;
    }

    item.files = item.files.concat(validFiles);

    return;
  }

  /**
   * ### バリデーション
   *
   * - 各ファイルのバリデーションを行い、エラーの場合は空の配列を返す
   * - バリデーションのkey名はファイル名とする
   * - 最大ファイル数以上の場合は空の配列を返す
   */
  public async validation(files: Array<File>, item: SeparateFileSelectTableItem): Promise<Array<File>> {
    let result: Array<File> = [];

    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.$validator.errors.remove(item.residentSortKey);

    for (let file of files) {
      const fileSizeLimit: number = 1024 * 1024 * 50; // 50MBまでとする
      if (file.type != "application/pdf") {
        this.$validator.errors.add({ field: item.residentSortKey, msg: `PDFファイル以外は登録できません` });
        return [];
      } else if (fileSizeLimit < file.size) {
        this.$validator.errors.add({ field: item.residentSortKey, msg: `添付文書のファイルサイズが50MBを超えているため登録できません` });
        return [];
      } else {
        result.push(file);
      }
    }

    // 登録数上限は自前でバリデーションする
    if (9 < result.length + item.files.length) {
      this.$validator.errors.add({ field: item.residentSortKey, msg: `最大登録ファイル数以上は登録できません` });
      return [];
    }

    return result;
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  public async onPreview(item: SeparateFileSelectTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const modal: SendMessageFilePreviewModal | Vue | Element | Vue[] | Element[] = this.$refs.previewModal;
    if (modal instanceof SendMessageFilePreviewModal) {
      const previewSeparateFiles: File[] = [];
      for (const file of item.files) {
        const _file: any = file;
        // 一時保存されていたファイルの場合、初期処理でFileオブジェクトではなく、nameとurlのObject型のデータを格納している
        // urlがある場合はS3からファイルをダウンロードし、Fileオブジェクトに変換する
        if (_file.url != null) {
          const updatedFile: File = await this.getS3File(_file.url, _file.name);
          previewSeparateFiles.push(updatedFile);
        } else {
          previewSeparateFiles.push(file);
        }
      }
      // S3からファイル取得したものも含めて、filesを更新する
      item.files = previewSeparateFiles;

      modal.show("", false, item.files);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  public enablePreview(item: SeparateFileSelectTableItem): boolean {
    if (0 < item.files.length) {
      return true;
    } else {
      return false;
    }
  }

  public onFileDelete(index: number, item: SeparateFileSelectTableItem): void {
    item.files.splice(index, 1);
    return;
  }
}

/** テーブルの列名の情報 */
export interface SeparateFileSelectTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface SeparateFileSelectTableItem {
  residentSortKey: string;
  groupId: number;
  groupName: string;
  residentId: number;
  residentName: string;
  room: string;
  insuredNumberKey: string;

  targetFamilies: Array<SendTarget>;
  files: Array<File>;
}
</script>

<style lang="scss" scoped>
.resident-table-component {
  .family-detail-btn,
  .print-btn,
  .edit-btn,
  .delete-btn {
    width: 64px;
  }

  .edit-btn {
    margin-right: 8px;
  }

  .family-count {
    word-break: break-all;
    width: 55px;
    text-align: center;
  }
  .family-detail {
    width: 64px;
    text-align: center;
  }
}
</style>
